import React from "react";
import { useRequest } from "ahooks";
import { groupBy } from "lodash";
import { validateShipping } from "@/services/ship";
import { preShip } from "@/services/api/orderShip";
import { ShipValidationRuleKey, ShipValidator } from "@/services/ship/type";
import { useModalStore } from "@/hooks/Modal/hooks";
import OrderDTO from "@/dto/order/OrderDTO";
import { useGlobalStore } from "@/store/hooks";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { Modal } from "antd";
import { buildValidationSuccessfulResult } from "@/services/ship/util";
import { getPreShipWaybillValidator } from "./validators/getPreShipWaybillValidator";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface UsePreShipOptions {
    /** 获取本次要操作的订单列表 */
    getSelectedOrders: () => OrderDTO[];
    /** 所有订单数据，用于某些校验（如运单校验） */
    allOrders?: OrderDTO[];
    /** 预发货完成后调用：传入发生错误的店铺列表 */
    onFinished: (errorDataSource: ErrorData[]) => void;
}

interface ErrorData {
    shopId: number;
    errorMsg: string;
}

function getPreShipValidator(extraContent?: React.ReactNode): ShipValidator {
    return (orders) => {
        return new Promise((resolve, rejected) => {
            Modal.confirm({
                title: "预发货不会立即发货，是否继续预发货",
                content: (
                    <div>
                        <div>
                            <div className="mb5">
                                需要立即发货的，请直接发货。需要有物流再发货的，预发货有物流再发货需要设置自动发货
                            </div>
                            <div className="text-error">
                                说明：自动发货目前仅支持圆通、中通、申通、韵达、顺丰、极兔、德邦、安能、京东快递、天天、跨越、众邮等快递打印的电子面单号，且不同平台支持的快递有差异，如果平台支持多包裹发货且订单有多运单号时会自动尝试使用多包裹发货！
                            </div>
                        </div>
                        {extraContent}
                    </div>
                ),
                okText: "继续预发货",
                cancelText: "取消",
                onOk() {
                    resolve(buildValidationSuccessfulResult(orders));
                },
                onCancel() {
                    rejected(new Error("用户取消操作"));
                },
            });
        });
    };
}

export function usePreShip(options: UsePreShipOptions) {
    const { getSelectedOrders, allOrders = [], onFinished } = options;
    const globalStore = useGlobalStore();
    const modalStore = useModalStore();
    const { runAsync, loading } = useRequest(preShip, { manual: true });
    const orderManagementStore = useOrderManagementStore();

    async function handlePreShip(extraContent?: React.ReactNode) {
        const orders = getSelectedOrders();
        if (!orders.length) return;

        //  校验
        const { canShip, canShipOrders } = await validateShipping({
            orders,
            globalStore,
            rule: {
                [ShipValidationRuleKey.checkDisabledPrintShipping]: true,
                [ShipValidationRuleKey.checkCannotShipTag]: true,
                [ShipValidationRuleKey.checkRefundingOrder]: true,
                [ShipValidationRuleKey.checkOrderItemPartQuantityShip]: true,
            },
            validators: [
                getPreShipWaybillValidator(allOrders, modalStore, orderManagementStore),
                getPreShipValidator(extraContent),
            ],
        });
        if (!canShip || !canShipOrders.length) return;

        const groups = groupBy(canShipOrders, "shopId");
        const errorDataSource: ErrorData[] = [];

        for (const canShipOrders of Object.values(groups)) {
            const shopId = canShipOrders[0].shopId;
            const shop = globalStore.shopMapping.get(shopId);

            if (!shop) {
                errorDataSource.push({ shopId, errorMsg: `店铺 ${shopId} 不存在` });

                continue;
            }

            const orderIds = canShipOrders.map((it) => it.id);

            try {
                await runAsync(shopId, orderIds);
            } catch (error) {
                errorDataSource.push({ shopId, errorMsg: `${shop.name}:${error.message}` });
            }

            try {
                collectEvent(DataTrackEvent.PRE_SHIP, {
                    shop_id: shop.id,
                    shop_name: shop.name,
                    shop_type: ShopTypeNameMap[shop.type],
                    order_count: orderIds.length,
                });
            } catch {}
        }

        onFinished(errorDataSource);
    }

    return {
        loading,
        handlePreShip,
    };
}
